/** Browser mirror of the problem report/status schemas. webMirrorContracts.test.ts pins the
 *  blocks to their core owners; runtime imports from src/ are forbidden by the web boundary. */
import { z } from 'zod';
import { NAME_RE } from './nameGrammar';
// mirror:problem-report:start
/** `subsystem.event`. `unclassified` is what cores sent before codes were declared; it stays valid
 *  because their outboxes and last-sent batches still hold it. */
const PROBLEM_CODE_RE = /^(?:[a-z][a-z0-9-]{0,31}\.[a-z][a-z0-9_]{0,47}|unclassified)$/;
/** One redacted, single-line message sample. */
const PROBLEM_SAMPLE_RE = /^[^\u0000-\u001f\u007f]+$/;
const PROBLEM_LIMITS = { entries: 50, bodyBytes: 16384, count: 1000000, samples: 3, sampleChars: 500, globalPerMinute: 600, originPerMinute: 60, licencePerMinute: 6, dailyRows: 100, dailyCount: 1000000, retentionMs: 14 * 86400000, clientExpiryMs: 7 * 86400000 } as const;
const PROBLEM_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROBLEM_VERSION_RE = /^(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})$/;

/** Numeric host sample only; unavailable readings are omitted rather than reported as zero. */
const hostQuantity = z.number().nonnegative().max(Number.MAX_SAFE_INTEGER);
const hostCapacity = z.strictObject({
  usedBytes: hostQuantity, totalBytes: hostQuantity.refine(value => value > 0),
}).refine(value => value.usedBytes <= value.totalBytes);
const problemHostResourcesSchema = z.strictObject({
  sampledAt: z.number().int().nonnegative().max(8_640_000_000_000_000),
  cpuPercent: z.number().min(0).max(100).optional(),
  ram: hostCapacity.optional(),
  io: z.strictObject({ readMBps: hostQuantity, writeMBps: hostQuantity }).optional(),
  disk: hostCapacity.optional(),
  network: z.strictObject({ rxMbps: hostQuantity, txMbps: hostQuantity }).optional(),
});
const problemEntrySchema = z.strictObject({
  code: z.string().regex(PROBLEM_CODE_RE), level: z.enum(['warn', 'error']),
  plugin: z.string().regex(NAME_RE),
  pluginVersion: z.string().regex(PROBLEM_VERSION_RE).nullable(),
  count: z.number().int().positive().max(PROBLEM_LIMITS.count),
  samples: z.array(z.string().max(PROBLEM_LIMITS.sampleChars).regex(PROBLEM_SAMPLE_RE)).min(1).max(PROBLEM_LIMITS.samples)
    .refine(samples => new Set(samples).size === samples.length).optional(),
  hostResources: problemHostResourcesSchema.optional(),
}).refine(entry => entry.hostResources === undefined || entry.code === 'runtime.event_loop_lag' && entry.plugin === 'none');
const problemReportSchema = z.strictObject({
  batchId: z.string().regex(PROBLEM_UUID_RE), coreVersion: z.string().regex(PROBLEM_VERSION_RE),
  entries: z.array(problemEntrySchema).min(1).max(PROBLEM_LIMITS.entries),
}).refine(b => new Set(b.entries.map(e => JSON.stringify([e.code, e.level, e.plugin, e.pluginVersion]))).size === b.entries.length);
// mirror:problem-report:end
// mirror:problem-status:start
const stateSchema = z.strictObject({
  lastSent: problemReportSchema.nullable(), sentAt: z.number().int().nonnegative().nullable(),
  dropped: z.number().int().nonnegative(), status: z.enum(['idle', 'sent', 'retry', 'credentials', 'unavailable', 'storage']),
  httpStatus: z.number().int().min(100).max(599).nullable(),
});
export const problemStatusSchema = stateSchema.extend({ queued: z.number().int().nonnegative() });
// mirror:problem-status:end
